"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createMainMachine = exports.makeConfig = exports.machineJSON = exports.canSkipApptInfoWithDeepLinkParams = exports.syncDeepLinkParamsToPatientRelAndPurpose = exports.createMachine = void 0;
const xstate_1 = require("xstate");
const States_1 = require("../../lib/constants/States");
const steps_1 = require("../../lib/constants/steps");
const covidVaccineUtils_1 = require("../../lib/covidVaccineUtils");
const Actions_1 = require("../../lib/constants/Actions");
const deepLinking_1 = require("../../lib/constants/deepLinking");
const Filters_1 = require("../../lib/constants/Filters");
const epicOSWUtils_1 = require("../../lib/epicOSWUtils");
const gxUtils_1 = require("../../lib/gxUtils");
const utils_1 = require("../../lib/utils");
const getInitialContext_1 = __importDefault(require("../getInitialContext"));
const apptInfo_1 = require("./apptInfo");
const apptSummary_1 = require("./apptSummary");
const apptTime_1 = require("./apptTime");
const oswApptInfo_1 = require("./oswApptInfo");
const patientInfo_1 = require("./patientInfo");
const provider_1 = require("./provider");
/**
 * Creates and returns a machine based on the step passed in
 *
 * @param {String} step
 * @param {MachineContext} ctx
 */
const createMachine = (step, ctx) => {
    const { providerId, provider, insuranceOptions, locations, appointmentQuery, selectedAppointment, csrfToken, isCaptchaVerified, patientRel, purpose, locationId, periodStart, ehr, epicOSWConfig, linkedGxAnswers, gx, useProviderInsuranceOnRegForm, covidVaccinePatientRegFormConfig, patientRegFormConfig, hidePatientRelConfig, ehrPatientList, services, selectedPatientIndex, customerCode, updatedFormFields, deepLinkingParams, visibility, dbEnabledProperty, productName, apptTimeFilters, bookedAppt, backToSearchPageURL, featureFlags, darkshipFeatureFlags, searchMatchEnabled, appointmentEhrId, apiBaseURL, waitlistStatus } = ctx;
    const machines = {
        [States_1.loadingProvider]: () => (0, provider_1.createProviderMachine)({
            providerId,
            insuranceOptions,
            epicOSWConfig,
            covidVaccinePatientRegFormConfig,
            patientRegFormConfig,
            ehr,
            services,
            ehrPatientList,
            customerCode,
            productName,
            gx,
            useProviderInsuranceOnRegForm,
            purpose,
            deepLinkingParams,
            visibility,
            dbEnabledProperty,
            backToSearchPageURL,
            apiBaseURL
        }),
        [States_1.apptInfo]: () => (0, apptInfo_1.createApptInfoMachine)({
            provider,
            isCaptchaVerified,
            csrfToken,
            patientRel,
            purpose,
            appointmentQuery,
            gx,
            services,
            hidePatientRelConfig,
            customerCode,
            visibility,
            featureFlags,
            appointmentEhrId,
            locationId,
            suggestAltProviderEnabled: featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.dbw_suggest_alt_providers_for_step_1,
            apiBaseURL,
            searchMatchEnabled
        }),
        [States_1.apptTime]: () => (0, apptTime_1.createApptTimeMachine)({
            apptTimeFilters,
            provider,
            locations,
            appointmentQuery,
            locationId,
            periodStart,
            isCaptchaVerified,
            customerCode,
            csrfToken,
            visibility,
            services,
            productName,
            featureFlags,
            darkshipFeatureFlags,
            apiBaseURL
        }),
        [States_1.patientInfo]: () => (0, patientInfo_1.createPatientInfoMachine)({
            provider,
            insuranceOptions,
            appointmentQuery,
            selectedAppointment,
            updatedFields: Object.assign(Object.assign({}, updatedFormFields), (0, gxUtils_1.formFieldsFromGxAnswers)(linkedGxAnswers)),
            linkedGxAnswers,
            covidVaccinePatientRegFormConfig,
            patientRegFormConfig,
            isCaptchaVerified,
            csrfToken,
            ehr,
            ehrPatientList,
            selectedPatientIndex,
            services,
            customerCode,
            featureFlags,
            appointmentEhrId,
            apiBaseURL
        }),
        [States_1.apptSummary]: () => (0, apptSummary_1.createApptSummaryMachine)({
            bookedAppt,
            provider,
            selectedAppointment,
            visibility,
            featureFlags,
            apiBaseURL,
            waitlistStatus
        }),
        [States_1.oswApptInfo]: () => (0, oswApptInfo_1.createOSWApptInfoMachine)({ provider, epicOSWConfig })
    };
    return machines[step]();
};
exports.createMachine = createMachine;
/**
 * A new service should be created if it doesn't exist or
 * if we want to reset its current state e.g:
 *  - apptTime should always be new, to avoid stale appointment data
 * @param {String} step
 * @param {MachineContext} ctx
 */
const shouldCreateNewService = ({ step, ctx }) => {
    const alwaysNewServiceSteps = [States_1.apptInfo, States_1.apptTime, States_1.patientInfo];
    return alwaysNewServiceSteps.includes(step) || !ctx.steps[step];
};
/**
 * Everytime we go into the `apptTime`, we'll be re-fetching appointments
 * and the currently selected appointment may not exist anymore
 * @param {String} step
 */
const shouldResetSelectedAppt = (step) => {
    const steps = [States_1.apptTime, States_1.apptInfo];
    return steps.includes(step);
};
/**
 * Creates a service for the given step and a updates the current context
 * @param {String} step
 */
const createService = (step) => (0, xstate_1.assign)((ctx) => {
    if (!shouldCreateNewService({ step, ctx })) {
        return Object.assign(Object.assign({}, ctx), { curStep: step });
    }
    return Object.assign(Object.assign({}, ctx), { curStep: step, selectedAppointment: shouldResetSelectedAppt(step)
            ? null
            : ctx.selectedAppointment, steps: Object.assign(Object.assign({}, ctx.steps), { [step]: (0, xstate_1.spawn)((0, exports.createMachine)(step, ctx)) }) });
});
// --------
// Actions
// --------
/**
 * Parses context and returns what should be used as `patientRel`
 * - try to pull it from deep link params
 * - use a default patient relationship type based on customer config
 * - return what is in ctx
 */
const parsePatientRelFromCtx = (ctx) => {
    const { deepLinkingParams, hidePatientRelConfig, provider } = ctx;
    if (deepLinking_1.VALID_PATIENT_RELATIONSHIPS.includes(deepLinkingParams.patientRel)) {
        return deepLinkingParams.patientRel;
    }
    if ((0, utils_1.hidePatientRel)(provider, hidePatientRelConfig)) {
        return Filters_1.DEFAULT_PATIENT_REL;
    }
    return ctx.patientRel;
};
/**
 * Sync any data from Deep linking params and assign values to the following fields in context:
 * - purpose
 * - patientRel
 * - appointmentQuery
 */
const syncDeepLinkParamsToPatientRelAndPurpose = (ctx) => {
    const { deepLinkingParams, provider } = ctx;
    const changes = {
        patientRel: parsePatientRelFromCtx(ctx)
    };
    if (changes.patientRel && deepLinkingParams.purpose) {
        // We'll set purpose either way and only set appointmentQuery if the purpose has availability
        // If there isn't availability, the purpose will be auto-selected with an "unavailable to book" banner
        changes.purpose = Object.assign(Object.assign({}, ctx.purpose), { name: deepLinkingParams.purpose });
        const providerPurposes = provider.availableApptPurposes;
        if (providerPurposes[changes.patientRel].has(changes.purpose.name)) {
            changes.appointmentQuery = {
                patientRel: changes.patientRel,
                purpose: changes.purpose
            };
        }
    }
    return changes;
};
exports.syncDeepLinkParamsToPatientRelAndPurpose = syncDeepLinkParamsToPatientRelAndPurpose;
const syncParamsFromDeepLink = (ctx) => {
    const { deepLinkingParams: { locationId, periodStart } } = ctx;
    return Object.assign(Object.assign(Object.assign({}, (0, exports.syncDeepLinkParamsToPatientRelAndPurpose)(ctx)), (locationId && { locationId })), (periodStart && { periodStart: (0, utils_1.formatApiDate)(periodStart) }));
};
const actions = {
    createApptInfoService: createService(States_1.apptInfo),
    createApptTimeService: createService(States_1.apptTime),
    createProviderService: createService(States_1.loadingProvider),
    createPatientInfoService: createService(States_1.patientInfo),
    createApptSummaryService: createService(States_1.apptSummary),
    createOSWApptInfoService: createService(States_1.oswApptInfo),
    setAppointmentQuery: (0, xstate_1.assign)({
        appointmentQuery: (ctx, ev) => ({
            patientRel: ev.data.patientRel,
            purpose: ev.data.purpose
        })
    }),
    setProvider: (0, xstate_1.assign)({
        provider: (ctx, ev) => ev.data.provider
    }),
    setInsuranceOptions: (0, xstate_1.assign)({
        insuranceOptions: (ctx, ev) => ev.data.insuranceOptions
    }),
    setLocations: (0, xstate_1.assign)({
        locations: (ctx, ev) => ev.data.locations
    }),
    setIsCaptchaVerified: (0, xstate_1.assign)({
        isCaptchaVerified: (ctx, ev) => ev.data.isCaptchaVerified
    }),
    setGx: (0, xstate_1.assign)({
        gx: (ctx, ev) => {
            // This will set keys like gx.v1 & gx.v2, and we need to include keys set prior in ctx.gx like contextualInfo
            return Object.assign(Object.assign({}, ctx.gx), ev.data.gx);
        }
    }),
    setLinkedGxAnswers: (0, xstate_1.assign)({
        linkedGxAnswers: (ctx, ev) => ev.data.linkedGxAnswers
    }),
    setSelectedAppointment: (0, xstate_1.assign)({
        selectedAppointment: (ctx, ev) => ev.data.selectedAppointment
    }),
    setEhrPatientList: (0, xstate_1.assign)({
        ehrPatientList: (_ctx, ev) => ev.data.ehrPatientList
    }),
    setSelectedPatientIndex: (0, xstate_1.assign)({
        selectedPatientIndex: (_, ev) => ev.data.selectedPatientIndex
    }),
    setPatientRegistration: (0, xstate_1.assign)({
        patientRegistration: (_, ev) => ev.data.patientRegistration
    }),
    setApptTimeFilters: (0, xstate_1.assign)({
        apptTimeFilters: ({ apptTimeFilters }, { data = {} }) => {
            return Object.assign(Object.assign({}, apptTimeFilters), data);
        }
    }),
    setBookedAppt: (0, xstate_1.assign)({
        bookedAppt: (ctx, ev) => ev.data.bookedAppt
    }),
    setWaitlistStatus: (0, xstate_1.assign)({
        waitlistStatus: (ctx, ev) => ev.data.waitlistStatus
    }),
    setStepsConfigurationType: (0, xstate_1.assign)({
        stepsConfigurationType: (ctx) => {
            const { provider } = ctx;
            return (0, covidVaccineUtils_1.isCovidVaccineProvider)(provider) && !ctx.covidUseDefaultStepOrder
                ? steps_1.STEPS_PMC_COVID_VACCINE
                : steps_1.STEPS_PMC_DEFAULT;
        }
    }),
    setInitialContextFromURL: (0, xstate_1.assign)(() => ({
        skipGX: (0, utils_1.shouldSkipGX)(),
        backToSearchPageURL: (0, utils_1.getBackToSearchPageUrl)()
    })),
    syncParamsFromDeepLink: (0, xstate_1.assign)((ctx) => {
        return syncParamsFromDeepLink(ctx);
    }),
    updateFormFields: (0, xstate_1.assign)({
        updatedFormFields: (_ctx, ev) => ev.data.updatedFields
    }),
    clearApptInfoDeepLinkingParams: (0, xstate_1.assign)((ctx) => {
        return {
            deepLinkingParams: Object.assign(Object.assign({}, ctx.deepLinkingParams), { purpose: null, patientRel: null })
        };
    }),
    // reset location id deeplink param
    resetLocationIdInCtx: (0, xstate_1.assign)(() => {
        const { locationId } = (0, getInitialContext_1.default)();
        return {
            locationId
        };
    }),
    // reset period start deeplink param
    resetPeriodStartInCtx: (0, xstate_1.assign)(() => {
        const { periodStart } = (0, getInitialContext_1.default)();
        return {
            periodStart
        };
    }),
    // if needed, implementation for this should be passed in when creating this machine
    onDidBook: (ctx) => {
        ctx.onDidBook &&
            ctx.onDidBook({
                appointment: ctx.bookedAppt,
                provider: ctx.provider,
                patientRegistration: ctx.patientRegistration,
                location: (0, utils_1.getApptLocation)(ctx.provider, ctx.selectedAppointment)
            });
    },
    updateApptPurpose: (0, xstate_1.assign)({
        purpose: (ctx, ev) => (Object.assign(Object.assign({}, ctx.purpose), ev.data.purpose))
    })
};
// --------
// Guards
// --------
const hasGxQuestions = (ctx) => {
    if (ctx.skipGX) {
        return false;
    }
    return Boolean(ctx.gx.v1 || ctx.gx.v2 || ctx.gx.v2Conditionals);
};
const canSkipApptInfoWithDeepLinkParams = (ctx) => {
    const { deepLinkingParams: { patientRel, purpose }, provider } = ctx;
    if (!ctx.skipGX) {
        // if we have gx questions enabled we can't move forward
        if (hasGxQuestions(ctx)) {
            return false;
        }
    }
    return (deepLinking_1.VALID_PATIENT_RELATIONSHIPS.includes(patientRel) &&
        provider.availableApptPurposes[patientRel].has(purpose));
};
exports.canSkipApptInfoWithDeepLinkParams = canSkipApptInfoWithDeepLinkParams;
const guards = {
    hasApptInfoDeepLinkingParams: (ctx) => {
        const { deepLinkingParams: { purpose, patientRel } } = ctx;
        return Boolean(purpose || patientRel);
    },
    isGotoApptInfo: (ctx, ev) => ev.data.step === States_1.apptInfo,
    isGotoApptTime: (ctx, ev) => ev.data.step === States_1.apptTime,
    isGotoPatientInfo: (ctx, ev) => ev.data.step === States_1.patientInfo,
    isValidGotoReviewAppt: (ctx, ev) => 
    // only allow goto review when we have a selected appointment
    ev.data.step === States_1.reviewAppt && !!ctx.selectedAppointment,
    shouldShowApptSummary: (ctx) => ctx.showApptSummary,
    isOSWEnabled: (ctx) => {
        const { provider, epicOSWConfig } = ctx;
        return (0, epicOSWUtils_1.isOSWEnabled)(provider, epicOSWConfig);
    },
    hasNoSelectedAppointment: (ctx) => !ctx.selectedAppointment,
    hasBookedAppointment: (ctx) => ctx.bookedAppt,
    // is the next step (relative to curStep) patienInfo according to the current stepsConfiguration?
    isNextStepPatientInfo: (ctx) => (0, utils_1.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === States_1.patientInfo,
    // is the next step (relative to curStep) apptTime according to the current stepsConfiguration?
    isNextStepApptTime: (ctx) => (0, utils_1.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === States_1.apptTime,
    // is the next step (relative to curStep) reviewAppt according to the current stepsConfiguration?
    isNextStepReviewAppt: (ctx) => (0, utils_1.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === States_1.reviewAppt,
    shouldPatientInfoRedirectToApptTime: (ctx) => {
        // patient info should redirect to appt time if appt time comes before patient info
        // and there is no appointment selected. This could happen during browser "forward" navigation
        // after going back to appt time where the selected appointment got cleared
        const isApptTimePrevious = (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, ctx.curStep) >
            (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, States_1.apptTime);
        return isApptTimePrevious && !ctx.selectedAppointment;
    },
    shouldApptInfoSkipToApptTime: (ctx) => {
        // appt info can skip to appt time when the appt time step is next from app info and we have deeplinking params
        const apptInfoIndex = (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, States_1.apptInfo);
        const apptTimeIndex = (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, States_1.apptTime);
        if (apptTimeIndex !== apptInfoIndex + 1) {
            // if apptTime doesn't immediately follow apptInfo, bail out now
            return false;
        }
        // check if we have valid deeplinking params
        return (0, exports.canSkipApptInfoWithDeepLinkParams)(ctx);
    },
    shouldApptInfoSkipToPatientInfo: (ctx) => {
        // appt info can skip to patient info when the appt time step is next from app info and we have deeplinking params
        const apptInfoIndex = (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, States_1.apptInfo);
        const patientInfoIndex = (0, utils_1.getStepIndex)(ctx.stepsConfigurationType, States_1.patientInfo);
        if (patientInfoIndex !== apptInfoIndex + 1) {
            // if patientInfo doesn't immediately follow apptInfo, bail out now
            return false;
        }
        return (0, exports.canSkipApptInfoWithDeepLinkParams)(ctx);
    }
};
const machineJSON = () => ({
    id: 'Machine__main',
    initial: States_1.initializing,
    // NOTE: this context is expected to be overriden
    // when this machine gets intialized
    // @see getInitialContext
    context: {},
    on: {
        [Actions_1.GO_BACK]: [
            // 'back' stays on appt summary if we have a booked appt
            { target: States_1.apptSummary, cond: 'hasBookedAppointment' },
            { target: States_1.apptInfo, cond: 'isGotoApptInfo' },
            { target: States_1.apptTime, cond: 'isGotoApptTime' },
            { target: States_1.patientInfo, cond: 'isGotoPatientInfo' }
        ],
        [Actions_1.REBOOK]: {
            target: States_1.apptTime
        }
    },
    states: {
        [States_1.initializing]: {
            on: {
                [Actions_1.INITIALIZED]: {
                    actions: ['setInitialContextFromURL'],
                    target: States_1.loadingProvider
                }
            }
        },
        [States_1.loadingProvider]: {
            entry: 'createProviderService',
            on: {
                [Actions_1.NEXT]: {
                    target: States_1.routing,
                    actions: [
                        'setProvider',
                        'setInsuranceOptions',
                        'setLocations',
                        'setIsCaptchaVerified',
                        'setGx',
                        'setStepsConfigurationType',
                        'setEhrPatientList',
                        'setSelectedPatientIndex',
                        'updateApptPurpose'
                    ]
                }
            }
        },
        [States_1.routing]: {
            initial: States_1.checkingIsOSWEnabled,
            states: {
                [States_1.checkingIsOSWEnabled]: {
                    always: [
                        {
                            cond: 'isOSWEnabled',
                            target: `#${States_1.oswApptInfo}`
                        },
                        {
                            target: States_1.deepLinking
                        }
                    ]
                },
                [States_1.deepLinking]: {
                    always: [
                        {
                            actions: ['syncParamsFromDeepLink'],
                            target: `#${States_1.apptInfo}`
                        }
                    ]
                }
            }
        },
        [States_1.oswApptInfo]: {
            id: States_1.oswApptInfo,
            entry: 'createOSWApptInfoService'
        },
        [States_1.apptInfo]: {
            id: States_1.apptInfo,
            entry: 'createApptInfoService',
            always: [
                {
                    cond: 'shouldApptInfoSkipToApptTime',
                    actions: ['clearApptInfoDeepLinkingParams'],
                    target: States_1.apptTime
                },
                {
                    cond: 'shouldApptInfoSkipToPatientInfo',
                    actions: ['clearApptInfoDeepLinkingParams'],
                    target: States_1.patientInfo
                },
                {
                    cond: 'hasApptInfoDeepLinkingParams',
                    actions: ['clearApptInfoDeepLinkingParams']
                }
            ],
            on: {
                [Actions_1.NEXT]: [
                    {
                        cond: 'isNextStepApptTime',
                        target: States_1.apptTime,
                        actions: [
                            'setIsCaptchaVerified',
                            'setLinkedGxAnswers',
                            'setAppointmentQuery'
                        ]
                    },
                    {
                        cond: 'isNextStepPatientInfo',
                        target: States_1.patientInfo,
                        actions: [
                            'setIsCaptchaVerified',
                            'setLinkedGxAnswers',
                            'setAppointmentQuery'
                        ]
                    }
                ]
            }
        },
        [States_1.apptTime]: {
            id: States_1.apptTime,
            entry: ['createApptTimeService'],
            on: {
                [Actions_1.CAPTCHA_VERIFIED]: {
                    actions: 'setIsCaptchaVerified'
                },
                [Actions_1.RESET_LOCATION_FILTER]: {
                    actions: ['resetLocationIdInCtx']
                },
                [Actions_1.SET_FILTERS_ON_PARENT]: {
                    actions: ['setApptTimeFilters']
                },
                [Actions_1.NEXT]: [
                    {
                        target: States_1.patientInfo,
                        actions: [
                            /* this enables a user to select a new appointment by going back in the flow
                               if periodStart continued to live in ctx
                               you would not be able to move backward to apptTime from patientInfo in the deepLinking use case */
                            'resetPeriodStartInCtx',
                            'setSelectedAppointment'
                        ]
                    }
                ]
            }
        },
        [States_1.patientInfo]: {
            entry: 'createPatientInfoService',
            // if we landed on patient info without an appointment, send user back to
            // apptTime to select one
            // only if the next step is reviewAppt, otherwise apptTime is the next step
            always: [
                {
                    cond: 'shouldPatientInfoRedirectToApptTime',
                    target: `#${States_1.apptTime}`
                }
            ],
            states: {
                [States_1.routing]: {
                    always: [
                        { cond: 'shouldShowApptSummary', target: States_1.done },
                        { target: States_1.didBook }
                    ]
                },
                [States_1.didBook]: {},
                [States_1.done]: {
                    type: 'final'
                }
            },
            on: {
                [Actions_1.FORM_UPDATE]: {
                    actions: 'updateFormFields'
                },
                [Actions_1.NEXT]: [
                    {
                        cond: 'isNextStepApptTime',
                        target: States_1.apptTime,
                        actions: 'setPatientRegistration'
                    },
                    {
                        target: `.${States_1.routing}`,
                        actions: [
                            'setPatientRegistration',
                            'setBookedAppt',
                            'onDidBook',
                            'setWaitlistStatus'
                        ]
                    }
                ]
            },
            onDone: States_1.apptSummary
        },
        [States_1.apptSummary]: {
            entry: 'createApptSummaryService'
        }
    }
});
exports.machineJSON = machineJSON;
const makeConfig = () => ({ actions, guards });
exports.makeConfig = makeConfig;
const createMainMachine = ({ context, config = (0, exports.makeConfig)() }) => (0, xstate_1.Machine)((0, exports.machineJSON)()).withConfig(config).withContext(context);
exports.createMainMachine = createMainMachine;
//# sourceMappingURL=main.js.map